import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Search, Trash2, RotateCcw, ThumbsUp, ThumbsDown, ShoppingCart, Download, Package, Truck, Edit3 } from "lucide-react";
import { fmtTRY, fmtDate } from "@/lib/format";
import { generateQuotePDF } from "@/lib/quotePdf";
import SupplierOrderDialog from "@/components/SupplierOrderDialog";
import QuoteEditModal from "@/components/QuoteEditModal";

/* Fiyat Teklifleri — tüm carilere ait teklifleri tek yerden yönet.
   Filtreler: durum (all/pending/won/lost), cari, tarih aralığı, arama.
   Aksiyonlar: Olumlu/Olumsuz işaretleme, Beklemeye alma, PDF, Sipariş Aç (Olumlu için), Sil.
*/

export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState({}); // quote_id -> [SupplierOrder]
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [contactFilter, setContactFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const [orderDialogFor, setOrderDialogFor] = useState(null);
  const [editingFor, setEditingFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: qs }, { data: cs }, { data: os }, { data: co }] = await Promise.all([
        api.get("/price-quotes"),
        api.get("/contacts"),
        api.get("/supplier-orders").catch(() => ({ data: [] })),
        api.get("/settings/company").catch(() => ({ data: null })),
      ]);
      setRows(qs || []);
      setContacts(cs || []);
      const grouped = {};
      (os || []).forEach((o) => {
        if (o.quote_id) (grouped[o.quote_id] = grouped[o.quote_id] || []).push(o);
      });
      setOrders(grouped);
      setCompany(co || null);
    } catch (e) {
      toast.error("Teklifler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const contactMap = useMemo(() => {
    const m = {};
    contacts.forEach((c) => { m[c.id] = c; });
    return m;
  }, [contacts]);
  const customers = useMemo(
    () => contacts.filter((c) => c.type === "customer" || c.type === "both"),
    [contacts]
  );

  const filtered = useMemo(() => {
    const qs = q.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => {
      const status = r.status || "pending";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (contactFilter && r.contact_id !== contactFilter) return false;
      if (dateFrom && (r.date || "") < dateFrom) return false;
      if (dateTo && (r.date || "") > dateTo) return false;
      if (qs) {
        const hay = [
          r.title, r.stock_sku, r.stock_name, r.notes, r.flute, r.quality,
          r.supplier_name, contactMap[r.contact_id]?.name,
        ].filter(Boolean).join(" ").toLocaleLowerCase("tr");
        if (!hay.includes(qs)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, contactFilter, dateFrom, dateTo, q, contactMap]);

  const totals = useMemo(() => {
    let count = 0, amount = 0;
    filtered.forEach((r) => { count += 1; amount += Number(r.grand_total || 0); });
    return { count, amount };
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, won: 0, lost: 0 };
    rows.forEach((r) => { const s = r.status || "pending"; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [rows]);

  const setStatus = async (row, next) => {
    try {
      await api.patch(`/price-quotes/${row.id}/status`, { status: next });
      toast.success(next === "won" ? "Olumlu olarak işaretlendi"
                    : next === "lost" ? "Olumsuz olarak işaretlendi"
                    : "Beklemeye alındı");
      await load();
    } catch (e) {
      toast.error("Güncellenemedi: " + (e?.response?.data?.detail || e?.message));
    }
  };
  const remove = async (row) => {
    if (!window.confirm("Teklif silinsin mi? Bu işlem geri alınamaz.")) return;
    try {
      await api.delete(`/price-quotes/${row.id}`);
      toast.success("Silindi");
      await load();
    } catch { toast.error("Silinemedi"); }
  };
  const downloadPdf = async (row) => {
    try {
      await generateQuotePDF(row, contactMap[row.contact_id] || { name: "—" }, company);
      toast.success("PDF indirildi");
    } catch (e) { toast.error("PDF üretilemedi: " + (e?.message || "")); }
  };
  const resetFilters = () => {
    setStatusFilter("all"); setContactFilter(""); setDateFrom(""); setDateTo(""); setQ("");
  };
  const onOrderCreated = () => { setOrderDialogFor(null); load(); };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText size={20} />
            <h1 className="font-display text-3xl font-bold">Fiyat Teklifleri</h1>
          </div>
          <p className="text-sm text-neutral-500">
            Tüm carilere ait teklifleri tek yerden yönet — Olumlu/Olumsuz işaretle, PDF indir, tedarikçiye sipariş aç.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Filtreli Toplam</div>
          <div className="font-mono-num text-xl font-bold" data-testid="quotes-total-amount">
            {fmtTRY(totals.amount)}
          </div>
          <div className="text-[10px] text-neutral-500 font-mono-num">{totals.count} teklif</div>
        </div>
      </header>

      {/* Durum sekmeleri */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: "all", label: "Tümü", cls: "bg-neutral-800" },
          { key: "pending", label: "Bekleyen", cls: "bg-neutral-500" },
          { key: "won", label: "Olumlu", cls: "bg-emerald-700" },
          { key: "lost", label: "Olumsuz", cls: "bg-rose-700" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`quotes-tab-${t.key}`}
            onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border-2 transition-colors ${
              statusFilter === t.key
                ? `${t.cls} text-white border-transparent`
                : "border-black/20 text-neutral-700 hover:border-black"
            }`}
          >
            {t.label}
            <span className={`ml-2 text-[10px] px-1 py-0.5 ${
              statusFilter === t.key ? "bg-white/20" : "bg-neutral-100"
            }`}>{counts[t.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filtreler */}
      <div className="brutal-card p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <div className="label-caps mb-1">Ara</div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="quotes-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Başlık, cari, tedarikçi, karton, stok…"
              className="input pl-7"
            />
          </div>
        </div>
        <div>
          <div className="label-caps mb-1">Müşteri</div>
          <select
            data-testid="quotes-contact-filter"
            value={contactFilter}
            onChange={(e) => setContactFilter(e.target.value)}
            className="input min-w-[200px] bg-white"
          >
            <option value="">Tümü</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-caps mb-1">Tarih Başlangıç</div>
          <input type="date" className="input" value={dateFrom}
                 data-testid="quotes-date-from"
                 onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <div className="label-caps mb-1">Tarih Bitiş</div>
          <input type="date" className="input" value={dateTo}
                 data-testid="quotes-date-to"
                 onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button
          onClick={resetFilters}
          data-testid="quotes-reset-filters"
          className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1.5"
        >
          <RotateCcw size={12} /> Sıfırla
        </button>
      </div>

      {/* Liste */}
      <div className="brutal-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b-2 border-black/60">
              <tr>
                <th className="text-left px-3 py-2 label-caps whitespace-nowrap">Tarih</th>
                <th className="text-left px-3 py-2 label-caps">Müşteri</th>
                <th className="text-left px-3 py-2 label-caps">Ürün / Karton</th>
                <th className="text-right px-3 py-2 label-caps">Adet</th>
                <th className="text-right px-3 py-2 label-caps">Toplam</th>
                <th className="text-left px-3 py-2 label-caps">Durum</th>
                <th className="text-left px-3 py-2 label-caps">Sipariş</th>
                <th className="w-48 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-neutral-500">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-neutral-500">
                  {rows.length === 0
                    ? "Henüz teklif yok. Fiyat Hesapla ekranından oluşturabilirsiniz."
                    : "Filtreye uyan teklif bulunamadı."}
                </td></tr>
              ) : filtered.map((r) => {
                const status = r.status || "pending";
                const relatedOrders = orders[r.id] || [];
                return (
                <tr key={r.id} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`quotes-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-num whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{contactMap[r.contact_id]?.name || "—"}</div>
                    {(() => {
                      const primary = (r.title && r.title.trim())
                        || (r.stock_name && r.stock_name.trim())
                        || (r.items && r.items[0] && ((r.items[0].title && r.items[0].title.trim()) || (r.items[0].stock_name && r.items[0].stock_name.trim())));
                      const extra = (r.items && r.items.length > 1) ? ` +${r.items.length - 1} ürün` : "";
                      return primary ? (
                        <div className="text-[11px] text-neutral-700 font-medium" data-testid={`quote-product-name-${r.id}`}>
                          {primary}{extra && <span className="text-neutral-400">{extra}</span>}
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(r.flute || r.quality) && (
                      <div><span className="font-semibold">{r.flute || "—"}</span>{r.quality && <> · {r.quality}</>}</div>
                    )}
                    {r.supplier_name && (
                      <div className="text-[10px] text-neutral-500 inline-flex items-center gap-1">
                        <Package size={9} /> {r.supplier_name}
                      </div>
                    )}
                    {r.length_mm != null && (
                      <div className="text-[10px] text-neutral-500 font-mono-num">
                        {r.length_mm}×{r.width_mm}×{r.height_mm} mm
                      </div>
                    )}
                    {r.shipping_total > 0 && (
                      <div className="text-[10px] text-neutral-500 inline-flex items-center gap-1">
                        <Truck size={9} /> {fmtTRY(r.shipping_total)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold text-emerald-700">
                    {fmtTRY(r.grand_total)}
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={status} /></td>
                  <td className="px-3 py-2 text-xs">
                    {relatedOrders.length > 0 ? (
                      <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[10px] font-mono-num">
                        <ShoppingCart size={10} /> {relatedOrders.length}
                      </div>
                    ) : status === "won" ? (
                      <span className="text-[10px] text-neutral-400">—</span>
                    ) : (
                      <span className="text-[10px] text-neutral-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {status === "pending" ? (
                        <>
                          <button
                            onClick={() => setStatus(r, "won")}
                            data-testid={`quotes-set-won-${r.id}`}
                            title="Olumlu (kabul)"
                            className="p-1.5 hover:bg-emerald-50 text-emerald-700"
                          >
                            <ThumbsUp size={13} />
                          </button>
                          <button
                            onClick={() => setStatus(r, "lost")}
                            data-testid={`quotes-set-lost-${r.id}`}
                            title="Olumsuz (red)"
                            className="p-1.5 hover:bg-rose-50 text-rose-600"
                          >
                            <ThumbsDown size={13} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setStatus(r, "pending")}
                          data-testid={`quotes-reset-${r.id}`}
                          title="Beklemeye al"
                          className="p-1.5 hover:bg-neutral-100 text-neutral-600"
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                      {status === "won" && (
                        <button
                          onClick={() => setOrderDialogFor(r)}
                          data-testid={`quotes-open-order-${r.id}`}
                          title="Tedarikçiye Sipariş Aç"
                          className="p-1.5 hover:bg-amber-50 text-amber-700"
                        >
                          <ShoppingCart size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setEditingFor(r)}
                        data-testid={`quotes-edit-${r.id}`}
                        title="Düzenle"
                        className="p-1.5 hover:bg-sky-50 text-sky-700"
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        onClick={() => downloadPdf(r)}
                        data-testid={`quotes-pdf-${r.id}`}
                        title="PDF İndir"
                        className="p-1.5 hover:bg-neutral-100 text-neutral-700"
                      >
                        <Download size={13} />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`quotes-remove-${r.id}`}
                        title="Sil"
                        className="p-1.5 hover:bg-rose-50 text-rose-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {orderDialogFor && (
        <SupplierOrderDialog
          quote={orderDialogFor}
          onClose={() => setOrderDialogFor(null)}
          onCreated={onOrderCreated}
        />
      )}

      {editingFor && (
        <QuoteEditModal
          quote={editingFor}
          contacts={contacts}
          onClose={() => setEditingFor(null)}
          onSaved={() => load()}
        />
      )}

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:8px 10px;font-size:13px;outline:none;background:white}.input:focus{border-color:black}`}</style>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "won") return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5"><ThumbsUp size={9} /> Olumlu</span>;
  if (status === "lost") return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-700 text-white px-1.5 py-0.5"><ThumbsDown size={9} /> Olumsuz</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-neutral-500 text-white px-1.5 py-0.5">Bekliyor</span>;
}
